import { ICellRendererParams } from '@ag-grid-community/core';

import { ScheduleBusinessOrder } from '../../../../order/models/schedule-business-order';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import {
	CommonColDef,
	DimensionColDef,
	LinkColDef,
	NumericColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterDimensionFilterComponent } from '../../filter-type/datacenter-dimension-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';
import { DatacenterSuggestiveListFilterComponent } from '../../filter-type/datacenter-suggestive-list-filter.component';
import { ORDER_SERVER_URL } from '../../../../config';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';

const GET_SCHEDULE_BUSINESS_ORDER_URL = `${ ORDER_SERVER_URL }/v3/scheduleBusinessOrders`;
const linkToScheduleOrderCardCellRendererParams = {
	routerLink(params: ICellRendererParams<ScheduleBusinessOrder, unknown>) {
		if (!params.data) {
			return null;
		}
		const { data } = params;
		return {
			commands: [
				'/order-tracking',
				'schedule-business-order',
				'card',
				data.supplierOrderNumber
			]
		};
	}
};

export class ScheduleBusinessOrderDatacenterColumnDefinition {
	readonly SCHEDULE_BUSINESS_ORDER_REQUIRED_FIELDS = ['supplierOrderNumber'];

	readonly SCHEDULE_BUSINESS_ORDER_DATA_CENTER_DEFAULT_CONFIGURATION:
	Map<
		string,
		CommonColDef<ScheduleBusinessOrder>
		| CommonColDef<ScheduleBusinessOrder, string>
		| CommonColDef<ScheduleBusinessOrder, Date>
	>;

	constructor(private readonly translationService: TranslationService) {
		this.SCHEDULE_BUSINESS_ORDER_DATA_CENTER_DEFAULT_CONFIGURATION = new
		Map<
			string,
			CommonColDef<ScheduleBusinessOrder>
			| CommonColDef<ScheduleBusinessOrder, string>
			| CommonColDef<ScheduleBusinessOrder, Date>
		>([
			[
				'customerOrderNumber',
				new LinkColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.customer-order-number'),
					colId: 'customerOrderNumber',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const scheduleBusinessOrder = params.data;
						return scheduleBusinessOrder.customerOrderNumber;
					},
					field: 'customerOrderNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					minWidth: 150,
					cellRendererParams: linkToScheduleOrderCardCellRendererParams,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				}),
			],
			[
				'supplierOrderNumber',
				new LinkColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('order.supplier.order.number'),
					colId: 'supplierOrderNumber',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const scheduleBusinessOrder = params.data;
						return scheduleBusinessOrder.supplierOrderNumber;
					},
					field: 'supplierOrderNumber',
					minWidth: 150,
					cellRendererParams: linkToScheduleOrderCardCellRendererParams,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'steelgrade',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.standard-steel-grade'),
					field: 'steelgradeDescription',
					colId: 'steelgrade',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'dimension',
				new DimensionColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.dimension'),
					dimensionGetter(params) {
						return params.data;
					},
					colId: 'dimension',
					fields: ['thickness', 'width', 'length'],
					filter: DatacenterDimensionFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DIMENSION_COLUMN
				})
			],
			[
				'length',
				new NumericColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('batch-card.length'),
					field: 'length',
					colId: 'length',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'length-mm' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const scheduleBusinessOrder = params.data;
						if (!scheduleBusinessOrder.length) {
							return '';
						}
						return scheduleBusinessOrder.length;
					},
				})
			],
			[
				'thickness',
				new NumericColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.thickness'),
					field: 'thickness',
					colId: 'thickness',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'thickness' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'width',
				new NumericColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.width'),
					field: 'width',
					colId: 'width',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'width' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'mill',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.mill'),
					colId: 'mill',
					field: 'millDescription',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'soldTo',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.sold-to'),
					field: 'soldToDescription',
					colId: 'soldTo',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'customerArticleReference',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.customer-article-reference'),
					field: 'customerArticleReference',
					colId: 'customerArticleReference',
					minWidth: 100,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'endUser',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.end-user'),
					field: 'endUserDescription',
					colId: 'endUser',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'billTo',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('order.bill.to'),
					field: 'billToDescription',
					colId: 'billTo',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'productCategory',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.product-category'),
					field: 'productCategory',
					colId: 'productCategory',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const scheduleBusinessOrder = params.data;
						return scheduleBusinessOrder.productCategoryDescription;
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { saOrderFields: ['productCategory', 'productCategoryDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false
				})
			],
			[
				'shipTo',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.ship-to'),
					field: 'shipToDescription',
					colId: 'shipTo',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'stockPartner',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('schedule-business-order.stock-partner'),
					field: 'stockPartnerDescription',
					colId: 'stockPartner',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'businessStatus',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('schedule-business-order.sa-business-status'),
					field: 'businessStatus',
					colId: 'businessStatus',
					columnType: ColumnType.TRANSLATED_COLUMN,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { saOrderFields: ['businessStatus', 'businessStatusDescription'] },
					suppressHeaderMenuButton: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const scheduleBusinessOrder = params.data;
						return scheduleBusinessOrder.businessStatusDescription;
					},
					sortable: false
				})
			],
			[
				'colorBottomSide',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.color-bottom-side'),
					field: 'colorBottomSide',
					colId: 'colorBottomSide',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { saOrderFields: ['colorBottomSide', 'colorBottomSideDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const scheduleBusinessOrder = params.data;
						return scheduleBusinessOrder.colorBottomSideDescription;
					},
					columnType: ColumnType.TRANSLATED_COLUMN
				})
			],
			[
				'colorTopSide',
				new CommonColDef<ScheduleBusinessOrder>({
					headerName: this.translationService.getTR('datacenter-filter.color-top-side'),
					field: 'colorTopSide',
					colId: 'colorTopSide',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { saOrderFields: ['colorTopSide', 'colorTopSideDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const scheduleBusinessOrder = params.data;
						return scheduleBusinessOrder.colorTopSideDescription;
					},
					columnType: ColumnType.TRANSLATED_COLUMN
				})
			],
			[
				'orderManager',
				new CommonColDef({
					headerName: this.translationService.getTR('order.responsible.order.manager'),
					field: 'orderManagerDescription',
					colId: 'orderManager',
					filter: DatacenterSuggestiveListFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					filterParams: { saOrderFields: ['orderManager', 'orderManagerDescription'],
						url: GET_SCHEDULE_BUSINESS_ORDER_URL },
					sortable: false
				})
			],
		]);
	}
}

export const DEFAULT_VIEW_SCHEDULE_BUSINESS_ORDER_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'customerOrderNumber',
			pinned: 'left'
		},
		{
			colId: 'supplierOrderNumber',
			pinned: 'left'
		},
		{
			colId: 'steelgrade',
			pinned: 'left'
		},
		{
			colId: 'dimension',
			pinned: 'left'
		},
		{
			colId: 'soldTo',
			pinned: 'left'
		},
		{
			colId: 'mill'
		},
		{
			colId: 'productCategory'
		},
		{
			colId: 'customerArticleReference'
		},
		{
			colId: 'billTo'
		}
	],
	sortModel: [
		{
			colId: 'soldTo',
			sort: 'asc'
		}
	],
	filterState: [],
	type: 'schedule-business-order',
	isDefault: true,
	name: 'Default',
	id: -99
};
